import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button, dialogs, Icon, Input, Link } from '@nvision/ui';

import { Card } from '@/components/card';
import { Page } from '@/components/page';
import type { RootState } from '@/redux/RootReducer';

import { ROUTES } from '../../../config';
import { useCreateContract, useCreateContractPricing } from '../../../hooks';
import { getDefaultNewContract, type NewContract } from '../../../models';
import { ContractDetails, type ContractFormRef } from '../contract-details';
import { ContractDocuments } from '../contract-documents';
import { ContractPricing, type ContractPricingRef } from '../contract-pricing';

import styles from '../contract.module.css';

const NEW_CONTRACT_STATUS = 'New';

export const ContractNew: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['translation']);
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany, shallowEqual);

  const initialValues = useMemo<NewContract>(
    () => ({
      ...getDefaultNewContract(),
      customer: selectedCompany
        ? {
            id: Number(selectedCompany.companyId),
            label: String(selectedCompany.companyName ?? ''),
            name: String(selectedCompany.companyName ?? ''),
          }
        : null,
      status: NEW_CONTRACT_STATUS,
    }),
    [selectedCompany],
  );

  const formRef = useRef<ContractFormRef>({} as ContractFormRef);
  const pricingRef = useRef<ContractPricingRef>({} as ContractPricingRef);

  const { isPending: isLinkingPricing, mutate: createContractPricing } = useCreateContractPricing();

  const { isPending: isCreating, mutate: createContract } = useCreateContract({
    onSuccess: ({ key }) => {
      const openContract = () => navigate(ROUTES.contract(key), { replace: true });
      const { linkedPricing } = pricingRef.current;

      if (linkedPricing.length) {
        createContractPricing({ contractKey: key, pricing: linkedPricing }, { onSettled: openContract });
      } else {
        openContract();
      }
    },
  });

  const onClose = () => {
    if (!formRef.current.isDirty() && !pricingRef.current.linkedPricing.length) {
      navigate(-1);
      return;
    }

    dialogs.warning({
      actions: [
        {
          label: t('translation:default.buttons.close'),
          onPress: () => navigate(-1),
          variant: 'destructive',
        },
        {
          label: t('translation:default.buttons.stay'),
        },
      ],
      content: t('translation:default.fields.unsavedChanges'),
      title: t('translation:default.buttons.warning'),
    });
  };

  const onSubmit = (values: NewContract) => {
    createContract(values);
  };

  return (
    <Page>
      <Page.Main className={styles.main}>
        <Link className={styles.link} iconLeft={<Icon name="arrow-left" size={20} />} onClick={onClose}>
          Back to previous page
        </Link>
        <div className={styles.container}>
          <Card>
            <Card.Header
              title="Contract Information"
              actions={
                <>
                  <Input
                    value={NEW_CONTRACT_STATUS}
                    placeholder="Contract Status"
                    isDisabled
                    className={styles.statusField}
                  />
                  <div className={styles.divider} />
                  <Button secondary outline onClick={onClose}>
                    {t('translation:default.buttons.cancel')}
                  </Button>
                  <Button primary isLoading={isCreating || isLinkingPricing} onClick={() => formRef.current?.submit()}>
                    {t('translation:default.buttons.save')}
                  </Button>
                </>
              }
            />
            <Card.Main>
              <ContractDetails formRef={formRef} initialValues={initialValues} onSubmit={onSubmit} />
              <ContractPricing pricingRef={pricingRef} />
              <ContractDocuments />
            </Card.Main>
          </Card>
        </div>
      </Page.Main>
    </Page>
  );
};
